/**
 * LetsBONK Bonding Curve Trading
 *
 * Direct integration with LetsBONK's bonding curve program.
 * Similar mechanics to pump.fun but different program addresses.
 */

import {
  Connection,
  PublicKey,
  TransactionInstruction,
  SystemProgram,
} from '@solana/web3.js';
import {
  TOKEN_PROGRAM_ID,
  ASSOCIATED_TOKEN_PROGRAM_ID,
  getAssociatedTokenAddress,
} from '@solana/spl-token';

// LetsBONK Program IDs
export const LETSBONK_PROGRAM_ID = new PublicKey('BonkFunFVVfdxzQv5Nfgb9znwfPHRyBwPkvgskH8DKxk');
export const LETSBONK_GLOBAL = new PublicKey('BonkG1obaLtV3gvJEbSf3dwrmsTGFwYjhc7fFPPxVfq8');
export const LETSBONK_FEE_RECIPIENT = new PublicKey('BonkFeeRec1p1ent1111111111111111111111111111');

const SYSTEM_PROGRAM_ID = SystemProgram.programId;

/**
 * LetsBONK bonding curve state
 */
export interface LetsBonkCurveState {
  virtualTokenReserves: bigint;
  virtualSolReserves: bigint;
  realTokenReserves: bigint;
  realSolReserves: bigint;
  tokenTotalSupply: bigint;
  complete: boolean;
}

/**
 * Derive the bonding curve PDA for a LetsBONK token
 */
export function getBondingCurvePDA(mint: PublicKey): PublicKey {
  const [pda] = PublicKey.findProgramAddressSync(
    [Buffer.from('bonding-curve'), mint.toBuffer()],
    LETSBONK_PROGRAM_ID
  );
  return pda;
}

/**
 * Derive the bonding curve token account
 */
export function getBondingCurveTokenAccount(mint: PublicKey): PublicKey {
  const bondingCurve = getBondingCurvePDA(mint);
  const [pda] = PublicKey.findProgramAddressSync(
    [bondingCurve.toBuffer(), TOKEN_PROGRAM_ID.toBuffer(), mint.toBuffer()],
    ASSOCIATED_TOKEN_PROGRAM_ID
  );
  return pda;
}

/**
 * Fetch and parse bonding curve state
 */
export async function getBondingCurveState(
  connection: Connection,
  mint: PublicKey
): Promise<LetsBonkCurveState | null> {
  const bondingCurve = getBondingCurvePDA(mint);
  const accountInfo = await connection.getAccountInfo(bondingCurve);

  if (!accountInfo || accountInfo.data.length < 49) {
    return null;
  }

  const data = accountInfo.data;

  return {
    virtualTokenReserves: data.readBigUInt64LE(8),
    virtualSolReserves: data.readBigUInt64LE(16),
    realTokenReserves: data.readBigUInt64LE(24),
    realSolReserves: data.readBigUInt64LE(32),
    tokenTotalSupply: data.readBigUInt64LE(40),
    complete: data[48] === 1,
  };
}

/**
 * Calculate tokens received for SOL input (buy)
 */
export function calculateBuyOutput(
  solAmount: bigint,
  state: LetsBonkCurveState
): bigint {
  if (state.complete) {
    throw new Error('Token has graduated - use Raydium');
  }

  const numerator = solAmount * state.virtualTokenReserves;
  const denominator = state.virtualSolReserves + solAmount;

  return numerator / denominator;
}

/**
 * Calculate SOL received for token input (sell)
 */
export function calculateSellOutput(
  tokenAmount: bigint,
  state: LetsBonkCurveState
): bigint {
  if (state.complete) {
    throw new Error('Token has graduated - use Raydium');
  }

  const numerator = tokenAmount * state.virtualSolReserves;
  const denominator = state.virtualTokenReserves + tokenAmount;

  return numerator / denominator;
}

/**
 * Apply slippage tolerance
 */
export function applySlippage(amount: bigint, slippageBps: number): bigint {
  const slippageMultiplier = BigInt(10000 - slippageBps);
  return (amount * slippageMultiplier) / BigInt(10000);
}

/**
 * Build LetsBONK buy instruction
 */
export async function buildBuyInstruction(params: {
  mint: PublicKey;
  userWallet: PublicKey;
  solAmount: bigint;
  minTokensOut: bigint;
}): Promise<TransactionInstruction> {
  const { mint, userWallet, solAmount, minTokensOut } = params;

  const bondingCurve = getBondingCurvePDA(mint);
  const bondingCurveTokenAccount = getBondingCurveTokenAccount(mint);
  const userTokenAccount = await getAssociatedTokenAddress(mint, userWallet);

  // LetsBONK buy discriminator
  const BUY_DISCRIMINATOR = Buffer.from([102, 6, 61, 18, 1, 218, 235, 234]);

  const data = Buffer.alloc(8 + 8 + 8);
  BUY_DISCRIMINATOR.copy(data, 0);
  data.writeBigUInt64LE(minTokensOut, 8);
  data.writeBigUInt64LE(solAmount, 16);

  return new TransactionInstruction({
    programId: LETSBONK_PROGRAM_ID,
    keys: [
      { pubkey: LETSBONK_GLOBAL, isSigner: false, isWritable: false },
      { pubkey: LETSBONK_FEE_RECIPIENT, isSigner: false, isWritable: true },
      { pubkey: mint, isSigner: false, isWritable: false },
      { pubkey: bondingCurve, isSigner: false, isWritable: true },
      { pubkey: bondingCurveTokenAccount, isSigner: false, isWritable: true },
      { pubkey: userTokenAccount, isSigner: false, isWritable: true },
      { pubkey: userWallet, isSigner: true, isWritable: true },
      { pubkey: SYSTEM_PROGRAM_ID, isSigner: false, isWritable: false },
      { pubkey: TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },
    ],
    data,
  });
}

/**
 * Build LetsBONK sell instruction
 */
export async function buildSellInstruction(params: {
  mint: PublicKey;
  userWallet: PublicKey;
  tokenAmount: bigint;
  minSolOut: bigint;
}): Promise<TransactionInstruction> {
  const { mint, userWallet, tokenAmount, minSolOut } = params;

  const bondingCurve = getBondingCurvePDA(mint);
  const bondingCurveTokenAccount = getBondingCurveTokenAccount(mint);
  const userTokenAccount = await getAssociatedTokenAddress(mint, userWallet);

  // LetsBONK sell discriminator
  const SELL_DISCRIMINATOR = Buffer.from([51, 230, 133, 164, 1, 127, 131, 173]);

  const data = Buffer.alloc(8 + 8 + 8);
  SELL_DISCRIMINATOR.copy(data, 0);
  data.writeBigUInt64LE(tokenAmount, 8);
  data.writeBigUInt64LE(minSolOut, 16);

  return new TransactionInstruction({
    programId: LETSBONK_PROGRAM_ID,
    keys: [
      { pubkey: LETSBONK_GLOBAL, isSigner: false, isWritable: false },
      { pubkey: LETSBONK_FEE_RECIPIENT, isSigner: false, isWritable: true },
      { pubkey: mint, isSigner: false, isWritable: false },
      { pubkey: bondingCurve, isSigner: false, isWritable: true },
      { pubkey: bondingCurveTokenAccount, isSigner: false, isWritable: true },
      { pubkey: userTokenAccount, isSigner: false, isWritable: true },
      { pubkey: userWallet, isSigner: true, isWritable: true },
      { pubkey: SYSTEM_PROGRAM_ID, isSigner: false, isWritable: false },
      { pubkey: ASSOCIATED_TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },
      { pubkey: TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },
    ],
    data,
  });
}

/**
 * Get buy quote
 */
export async function getBuyQuote(
  connection: Connection,
  mint: PublicKey,
  solAmount: bigint,
  slippageBps: number
): Promise<{
  expectedTokens: bigint;
  minTokensOut: bigint;
  priceImpact: number;
  isGraduated: boolean;
}> {
  const state = await getBondingCurveState(connection, mint);

  if (!state) {
    throw new Error('Token not found on LetsBONK');
  }

  if (state.complete) {
    return {
      expectedTokens: BigInt(0),
      minTokensOut: BigInt(0),
      priceImpact: 0,
      isGraduated: true,
    };
  }

  const expectedTokens = calculateBuyOutput(solAmount, state);
  const minTokensOut = applySlippage(expectedTokens, slippageBps);

  const priceBefore = Number(state.virtualSolReserves) / Number(state.virtualTokenReserves);
  const priceAfter = Number(state.virtualSolReserves + solAmount) /
                     Number(state.virtualTokenReserves - expectedTokens);
  const priceImpact = ((priceAfter - priceBefore) / priceBefore) * 100;

  return {
    expectedTokens,
    minTokensOut,
    priceImpact,
    isGraduated: false,
  };
}

/**
 * Get sell quote
 */
export async function getSellQuote(
  connection: Connection,
  mint: PublicKey,
  tokenAmount: bigint,
  slippageBps: number
): Promise<{
  expectedSol: bigint;
  minSolOut: bigint;
  priceImpact: number;
  isGraduated: boolean;
}> {
  const state = await getBondingCurveState(connection, mint);

  if (!state) {
    throw new Error('Token not found on LetsBONK');
  }

  if (state.complete) {
    return {
      expectedSol: BigInt(0),
      minSolOut: BigInt(0),
      priceImpact: 0,
      isGraduated: true,
    };
  }

  const expectedSol = calculateSellOutput(tokenAmount, state);
  const minSolOut = applySlippage(expectedSol, slippageBps);

  const priceBefore = Number(state.virtualSolReserves) / Number(state.virtualTokenReserves);
  const priceAfter = Number(state.virtualSolReserves - expectedSol) /
                     Number(state.virtualTokenReserves + tokenAmount);
  const priceImpact = ((priceBefore - priceAfter) / priceBefore) * 100;

  return {
    expectedSol,
    minSolOut,
    priceImpact,
    isGraduated: false,
  };
}

/**
 * Check if token is a LetsBONK token
 */
export async function isLetsBonkToken(
  connection: Connection,
  mint: PublicKey
): Promise<boolean> {
  const state = await getBondingCurveState(connection, mint);
  return state !== null;
}

/**
 * Check if token has graduated
 */
export async function isGraduated(
  connection: Connection,
  mint: PublicKey
): Promise<boolean> {
  const state = await getBondingCurveState(connection, mint);
  return state?.complete ?? false;
}
